// deps
import { useCallback, useContext, useMemo, useRef, useState } from "react";

// internal-deps
import { WalletSpecificActions, PayLaterCreditLimitData } from "@swiggy-private/payment-ui";
import { captureError, isAndroidPlatform, useMountedRef } from "@swiggy-private/core-ui";
import {
    recordNREvent,
    getBridgeSdk,
    NEWRELIC_TABLES,
    WP_WALLET_BALANCE_STATE,
    GlobalHeaderState,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";

// interfaces
import {
    AmazonPayLinkWalletResponseData,
    AmazonPayMetadataResponseData,
    AmazonSdkGetBalanceResponseData,
} from "@swiggy-private/native-bridge";

// contexts
import { AmazonPaySdkContext, AmazonPaySdkContextValue } from "@payment-client/contexts/AmazonPaySdkContext";
import { getWalletBalancePublic, linkWalletPublic } from "@payment-client/apis/publicWallet";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// helpers
import {
    recordLinkingFailed,
    recordLinkWalletNBResponse,
    recordNativeBridgeCall,
} from "@payment-client/helpers/AmazonPayNREvents";
import { buildAmazonPayLinkParams } from "@payment-client/helpers/WalletHelper";
import { getPaylaterCreditLimit } from "@payment-client/apis/PublicPayLater";
import { CreditLimitBusinessError } from "@payment-client/interfaces/PublicPayLater";
import { buildAplState } from "@payment-client/helpers/AmazonPaySdkHelper";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { BOOLEAN_AS_STRING, PAYMENT_CODES, VALID_WALLET_LINK_STATUSES } from "@swiggy-private/payment-types";
import { DOWNSTREAM_HOSTS } from "@payment-client/constants/Hosts";
import { AMAZON_PAY_LINK_STATUS, AMAZON_PAY_NB_STATES, APAY_NB_CALL_STATUS } from "@payment-client/constants/AmazonPay";
import { getAmazonPayLaterInfo, AmazonPayLaterField } from "@payment-client/helpers/PaylaterHelper";

// types
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

interface UseAmazonPayHandlerReturnType {
    getAmazonPaySdkStatus: WalletSpecificActions["getAmazonPaySdkStatus"];
    linkAmazonPaySdk: WalletSpecificActions["linkAmazonPaySdk"];
    showLoader: boolean;
    getAmazonPayMetaId: WalletSpecificActions["getAmazonPayMetaId"];
}

export const useAmazonPayHandler = (paymentAmount: number): UseAmazonPayHandlerReturnType => {
    const isMountedRef = useMountedRef();
    const { state, setState, sdkFetchInProgress, setSdkFetchInProgress } = useContext(AmazonPaySdkContext);
    const [showLoader, setShowLoader] = useState(false);
    const { paymentResponse } = useContext(ShowPaymentContext);

    const isAmazonPayLaterAvailable = useMemo(() => {
        return (
            !isEmpty(paymentResponse) &&
            getAmazonPayLaterInfo(paymentResponse as GetPaymentMethodsResponse, AmazonPayLaterField.ENABLED_STATUS)
        );
    }, [paymentResponse]);

    const { isNewSdkAvailable = false } = state ?? {};

    // Tracks the last AmazonPay availability value logged to NR so we emit once when the user first
    // sees APay (getAmazonPaySdkStatus is consulted while rendering it) and again only if it flips —
    // rather than logging on every render.
    const apayStatusLoggedValueRef = useRef<boolean | null>(null);
    // metaId is not part of the SDK context state; it is only known once getAmazonPayMetaId runs, so we
    // stash its presence here. Defaults to "--" (unknown) until that bridge call has resolved.
    const hasMetaIdRef = useRef<string>("--");
    const getAmazonPaySdkStatus = useCallback(() => {
        const isAvailable = !!state?.available;
        // (2.8) AmazonPay SDK availability + metaId presence (once known via getAmazonPayMetaId).
        if (apayStatusLoggedValueRef.current !== isAvailable) {
            apayStatusLoggedValueRef.current = isAvailable;
            recordNREvent({
                eventType: NEWRELIC_TABLES.WP_WALLET_BALANCE,
                state: WP_WALLET_BALANCE_STATE.AMAZONPAY_SDK_STATUS_CHECK,
                isAvailable: String(isAvailable),
                isNewSdk: String(isNewSdkAvailable),
                hasMetaId: hasMetaIdRef.current,
            });
        }
        return { state, sdkFetchInProgress };
    }, [state, sdkFetchInProgress, isNewSdkAvailable]);

    const handleLinkWalletSuccess = useCallback(
        async (res: AmazonPayLinkWalletResponseData) => {
            if (res.status !== AMAZON_PAY_LINK_STATUS.GRANTED) {
                recordLinkWalletNBResponse(res.status);
                return;
            }

            try {
                // show instrument level loader
                setSdkFetchInProgress?.(true);
                // call payments to generate access token
                const tokenResponse = await linkWalletPublic(
                    DOWNSTREAM_HOSTS.PAYMENT_PRESENTATION_SERVICE_HOST,
                    PAYMENT_CODES.AMAZONPAY,
                    undefined,
                    buildAmazonPayLinkParams(res),
                );

                if (tokenResponse && (tokenResponse as { isApayLinked: boolean })?.isApayLinked) {
                    const balancePromise = getWalletBalancePublic(
                        DOWNSTREAM_HOSTS.PAYMENT_PRESENTATION_SERVICE_HOST,
                        PAYMENT_CODES.AMAZONPAY,
                    ).catch(() => null);

                    const aplCreditLimitPromise = isAmazonPayLaterAvailable
                        ? getPaylaterCreditLimit(
                              DOWNSTREAM_HOSTS.PAYMENT_PRESENTATION_SERVICE_HOST,
                              PAYMENT_CODES.AMAZONPAYLATER,
                              paymentAmount.toString(),
                          ).catch(() => null)
                        : Promise.resolve(null);

                    const [balanceResponse, aplCreditLimitResponse] = await Promise.all([
                        balancePromise,
                        aplCreditLimitPromise,
                    ]);

                    const balanceFetchFailed = balanceResponse == null;
                    const aplCreditFetchFailed = isAmazonPayLaterAvailable && aplCreditLimitResponse === null;
                    const isAplBusinessError =
                        isAmazonPayLaterAvailable &&
                        aplCreditLimitResponse != null &&
                        "isBusinessError" in aplCreditLimitResponse;

                    if (balanceFetchFailed && aplCreditFetchFailed) {
                        recordNativeBridgeCall({
                            nbState: AMAZON_PAY_NB_STATES.LINK_WALLET_V2,
                            isNewSdk: true,
                            status: APAY_NB_CALL_STATUS.FAILURE,
                            extra: {
                                balanceFetchFailed: String(balanceFetchFailed),
                                aplCreditFetchFailed: String(aplCreditFetchFailed),
                            },
                        });
                        return;
                    }

                    const isWalletLinked =
                        !balanceFetchFailed && balanceResponse?.linkStatus === VALID_WALLET_LINK_STATUSES.LINKED;
                    const aplCreditData =
                        !aplCreditFetchFailed && !isAplBusinessError
                            ? (aplCreditLimitResponse as PayLaterCreditLimitData | null)
                            : null;
                    const hasAplCredit = !!aplCreditData?.availableCredit;

                    const updatedState: Partial<AmazonPaySdkContextValue["state"]> = { available: true };

                    if (isWalletLinked) {
                        updatedState.linked = true;
                        updatedState.balance = `${balanceResponse?.balance}`;
                        updatedState.isBalanceSufficient = Number(balanceResponse?.balance) >= paymentAmount;
                    }

                    if (isAplBusinessError) {
                        updatedState.apl = {
                            linked: true,
                            isCreditLimitSufficient: false,
                            disableMessage: (aplCreditLimitResponse as CreditLimitBusinessError).statusMessage,
                        };
                    } else if (hasAplCredit) {
                        updatedState.apl = buildAplState(true, aplCreditData?.availableCredit, paymentAmount, {
                            keyFactStatementUrl: aplCreditData?.keyFactStatementUrl,
                        });
                    }

                    if ((isWalletLinked || hasAplCredit || isAplBusinessError) && state) {
                        setState({
                            ...state,
                            ...updatedState,
                        });
                    }

                    if (balanceFetchFailed || aplCreditFetchFailed) {
                        recordNativeBridgeCall({
                            nbState: AMAZON_PAY_NB_STATES.LINK_WALLET_V2,
                            isNewSdk: true,
                            status: APAY_NB_CALL_STATUS.FAILURE,
                            extra: {
                                balanceFetchFailed: String(balanceFetchFailed),
                                aplCreditFetchFailed: String(aplCreditFetchFailed),
                            },
                        });
                    }

                    recordNativeBridgeCall({
                        nbState: AMAZON_PAY_NB_STATES.LINK_WALLET_V2,
                        isNewSdk: true,
                        status: APAY_NB_CALL_STATUS.SUCCESS,
                        extra: {
                            isWalletLinked: String(isWalletLinked),
                            balance: String(balanceResponse?.balance ?? "--"),
                            isBalanceSufficient: String(Number(balanceResponse?.balance) >= paymentAmount),
                            aplCredit: aplCreditData?.availableCredit ?? "--",
                            isAPLLinked: String(hasAplCredit),
                            isAplBusinessError: String(isAplBusinessError),
                            isCreditLimitSufficient: String(Number(aplCreditData?.availableCredit) >= paymentAmount),
                        },
                    });
                } else {
                    recordLinkingFailed({
                        hasTokenResponse: String(!!tokenResponse),
                        isAmazonPayLaterAvailable: String(isAmazonPayLaterAvailable),
                    });
                }
                setSdkFetchInProgress?.(false);
            } catch (err) {
                captureError(err);
                setSdkFetchInProgress?.(false);
            }
        },
        [isAmazonPayLaterAvailable, paymentAmount, setSdkFetchInProgress, setState, state],
    );

    const linkAmazonPaySdk = useCallback(
        (methodName: string) => {
            setShowLoader(true);
            const requestPayload = {
                state: isNewSdkAvailable ? AMAZON_PAY_NB_STATES.LINK_WALLET_V2 : AMAZON_PAY_NB_STATES.LINK_WALLET,
                data: {
                    methodName,
                },
            };

            getBridgeSdk()
                .payUsingAmazonPay(requestPayload)
                .catch(() => {
                    if (isMountedRef.current == null) {
                        return;
                    }
                    if (isNewSdkAvailable || isAmazonPayLaterAvailable) {
                        return { status: AMAZON_PAY_LINK_STATUS.DENIED };
                    }
                    return { available: true, linked: false, isBalanceSufficient: false };
                })
                .then((res) => {
                    if (isMountedRef.current == null) {
                        return;
                    }

                    if (isNewSdkAvailable || isAmazonPayLaterAvailable) {
                        setShowLoader(false); // hide the full page payment loader
                        handleLinkWalletSuccess(res as AmazonPayLinkWalletResponseData);
                    } else {
                        const response = res as AmazonSdkGetBalanceResponseData;
                        let isBalanceSufficient = false;
                        if (response && response.linked && response.balance) {
                            isBalanceSufficient = Number(response.balance) >= paymentAmount;
                        }

                        setState({
                            ...res,
                            isBalanceSufficient,
                            available: true,
                        } as AmazonPaySdkContextValue["state"]);
                        setShowLoader(false);
                        recordNREvent({
                            eventType: NEWRELIC_TABLES.PAYMENT_NATIVE_BRIDGE_AMAZON_SDK,
                            state: AMAZON_PAY_NB_STATES.LINK_WALLET,
                            isWalletLinked: String(response?.linked || false),
                            balance: String(response?.balance || "--"),
                            isBalanceSufficient: String(isBalanceSufficient),
                            isNewSdk: BOOLEAN_AS_STRING.FALSE,
                        });
                    }
                });
        },
        [isNewSdkAvailable, isMountedRef, isAmazonPayLaterAvailable, handleLinkWalletSuccess, setState, paymentAmount],
    );

    const getAmazonPayMetaId = useCallback(async () => {
        if (isAndroidPlatform(GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform))) {
            try {
                const nbStart = Date.now();
                const paymentMetaIdResponse = (await getBridgeSdk().payUsingAmazonPay({
                    state: AMAZON_PAY_NB_STATES.GET_APAY_METADATA,
                })) as AmazonPayMetadataResponseData;

                // Cache metaId presence so the SDK status-check event (2.8) can report it on later checks.
                hasMetaIdRef.current = String(!!paymentMetaIdResponse?.paymentMethodMeta);

                recordNativeBridgeCall({
                    nbState: AMAZON_PAY_NB_STATES.GET_APAY_METADATA,
                    isNewSdk: true,
                    status: APAY_NB_CALL_STATUS.SUCCESS,
                    latencyMs: Date.now() - nbStart,
                    extra: { hasMetaId: hasMetaIdRef.current },
                });

                return paymentMetaIdResponse?.paymentMethodMeta;
            } catch (err) {
                hasMetaIdRef.current = "false";
                recordNativeBridgeCall({
                    nbState: AMAZON_PAY_NB_STATES.GET_APAY_METADATA,
                    isNewSdk: true,
                    status: APAY_NB_CALL_STATUS.FAILURE,
                });
                captureError(err);
            }
        }
    }, []);

    return { getAmazonPaySdkStatus, linkAmazonPaySdk, showLoader, getAmazonPayMetaId };
};
